/**
 * AerialMoveToTargetAction - behavior tree action for aerial movement to a target.
 */

import * as THREE from 'three';

import { AerialMovementComponent } from '../../../../../components/movement/AerialMovementComponent.js';
import { EngineClass } from '../../../../../systems/ClassRegistry.js';
import { BehaviorStatus } from '../../../BehaviorNode.js';
import { BehaviorAction, type BehaviorActionOptions } from '../../BehaviorAction.js';

import type { Actor } from '../../../../../actors/Actor.js';
import type { Blackboard } from '../../../blackboard/Blackboard.js';

export interface AerialMoveToTargetActionOptions extends BehaviorActionOptions {
  targetActorKey?: string;
  targetPositionKey?: string;
  acceptanceRadius?: number;
  hoverAboveTarget?: boolean;
  hoverHeightOffset?: number;
}

@EngineClass('AerialMoveToTargetAction')
export class AerialMoveToTargetAction extends BehaviorAction {
  private targetActorKey?: string;
  private targetPositionKey?: string;
  private acceptanceRadius: number;
  private hoverAboveTarget: boolean;
  private hoverHeightOffset: number;
  private movementComponent: AerialMovementComponent | null = null;

  constructor(options: AerialMoveToTargetActionOptions = {}) {
    super({
      name: 'AerialMoveToTarget',
      ...options
    });

    this.targetActorKey = options.hasOwnProperty('targetActorKey') ? options.targetActorKey : 'PlayerActor';
    this.targetPositionKey = options.targetPositionKey;
    this.acceptanceRadius = options.acceptanceRadius ?? 5.0;
    this.hoverAboveTarget = options.hoverAboveTarget ?? true;
    this.hoverHeightOffset = options.hoverHeightOffset ?? 15.0;
  }

  protected override onInitialize(blackboard: Blackboard): void {
    const owner = this.getOwner(blackboard);
    if (!owner) {
      return;
    }

    this.movementComponent = owner.getComponent(AerialMovementComponent);
    if (!this.movementComponent) {
      console.warn('AerialMoveToTargetAction: No AerialMovementComponent found on actor');
      return;
    }
  }

  protected override onEnter(blackboard: Blackboard): void {}

  protected override async onUpdate(blackboard: Blackboard, deltaTime: number): Promise<BehaviorStatus> {
    const owner = this.getOwner(blackboard);
    if (!owner || !this.movementComponent) {
      return BehaviorStatus.Failure;
    }

    // Get target position
    const targetPos = this.getTargetPosition(blackboard);
    if (!targetPos) {
      return BehaviorStatus.Failure;
    }

    // Adjust target position if hovering above target
    let adjustedTarget = targetPos.clone();
    if (this.hoverAboveTarget) {
      adjustedTarget.y += this.hoverHeightOffset;
    }

    // Set the target position for the aerial movement component
    this.movementComponent.setTargetPosition(adjustedTarget);

    // Check if we've reached the target
    const ownerPosition = new THREE.Vector3();
    owner.rootComponent.getWorldPosition(ownerPosition);
    const distance = ownerPosition.distanceTo(adjustedTarget);

    if (distance <= this.acceptanceRadius) {
      // Clear the target to stop movement
      this.movementComponent.clearTarget();
      return BehaviorStatus.Success;
    }

    return BehaviorStatus.Running;
  }

  protected override onExit(blackboard: Blackboard, status: BehaviorStatus): void {
    // Clear the target when exiting
    if (this.movementComponent) {
      this.movementComponent.clearTarget();
    }
  }

  private getTargetPosition(blackboard: Blackboard): THREE.Vector3 | null {
    if (this.targetActorKey) {
      const actor = blackboard.getValue<Actor>(this.targetActorKey);
      if (actor) {
        const position = new THREE.Vector3();
        actor.rootComponent.getWorldPosition(position);
        return position;
      }
    }

    if (this.targetPositionKey) {
      const position = blackboard.getValue<THREE.Vector3>(this.targetPositionKey);
      if (position) {
        return position.clone();
      }
    }

    return null;
  }
}
